import { useCallback } from "react";
import { productApi } from "@/resources/product";
import { landscapeApi } from "@/resources/landscape";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { keywordApi } from "@/resources/keyword";
import { useAddKeywordToTheme } from "@/resources/keyword-theme";
import { useAppData } from "@/contexts/AppDataContext";
import { toast } from "@/hooks/use-toast";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import { KeywordMasterListAction } from "@/constants";
import type { Keyword } from "@/types";

export interface UseKeywordRowActionsResult {
  /** Mirrors `onMasterList` (`KeywordBankContentV2.tsx:382-404`) for a single keyword row. */
  toggleMasterList: (keywordId: number, action: KeywordMasterListAction) => Promise<void>;
  /** Mirrors `onCreateLandscape` (`KeywordBankTable/index.tsx:93-99`). */
  addLandscape: (searchPhrase: string, heroAsin: string) => void;
  /** Mirrors `onAddToRankTracker` (`KeywordBankTable/index.tsx:101-119`). */
  addToRankTracker: (keyword: Keyword, heroAsin: string) => void;
  /** Mirrors `onRemoveFromRankTracker` (`KeywordBankTable/index.tsx:121-132`). */
  removeFromRankTracker: (keyword: Keyword) => void;
  /** Mirrors the Theme cell's `onAddKeywordToTheme` (`KeywordBankContentV2.tsx:927-929`). */
  addToTheme: (keywordId: number, themeId: number) => Promise<void>;
}

/**
 * Runs a single keyword row's master-list, landscape, rank-tracker and theme
 * actions for the A/B build tab's Keywords row controls (WEB-006), with the
 * same mutations, arguments and toasts the product Keywords tab uses for one
 * keyword. The product tab itself is not moved onto this hook -- see
 * `components/ab-test.md § Target state -- AUT-430 spec C`, *Row actions*.
 */
export function useKeywordRowActions(productId: number): UseKeywordRowActionsResult {
  const { selectedBrandId } = useAppData();

  const { mutateAsync: bulkUpdateMasterList } = productApi.useBulkUpdateMasterList();
  const { mutate: createLandscape } = landscapeApi.useCreateLandscape();
  const { mutate: addRankTrackerKeywords } = rankTrackerApi.useAddRankTrackerKeywords();
  const { mutate: deleteRankTrackerKeyword } = rankTrackerApi.useDeleteRankTrackerKeyword();
  const { mutateAsync: addKeywordToTheme } = useAddKeywordToTheme();

  const toggleMasterList = useCallback(
    async (keywordId: number, action: KeywordMasterListAction) => {
      const keywordIds = [keywordId];
      const isAdding = action === KeywordMasterListAction.ADD;
      toast({
        title: isAdding ? "Adding to Master List..." : "Removing from Master List...",
        description: `${isAdding ? "Adding" : "Removing"} ${keywordIds.length} keyword${keywordIds.length !== 1 ? "s" : ""} ${isAdding ? "to" : "from"} Master List.`,
      });
      try {
        const result = await bulkUpdateMasterList({ productId, keywordIds, action });
        const actuallyUpdated = result?.updated_count ?? keywordIds.length;
        if (actuallyUpdated > 0) {
          toast({
            title: isAdding ? "Added to Master List" : "Removed from Master List",
            description: `${actuallyUpdated} keyword${actuallyUpdated !== 1 ? "s" : ""} ${isAdding ? "added" : "removed"} successfully.`,
          });
        }
      } catch (error) {
        handleApiError(error, () => { toggleMasterList(keywordId, action); });
      }
    },
    [bulkUpdateMasterList, productId],
  );

  const addLandscape = useCallback(
    (searchPhrase: string, heroAsin: string) => {
      createLandscape({
        hero_asin: heroAsin,
        search_phrase: searchPhrase,
        brand_id: selectedBrandId,
      });
    },
    [createLandscape, selectedBrandId],
  );

  const addToRankTracker = useCallback(
    (keyword: Keyword, heroAsin: string) => {
      if (!selectedBrandId) return;
      addRankTrackerKeywords(
        {
          brandId: selectedBrandId,
          heroAsin,
          keywords: [keyword.search],
          source: "manual",
          // Target the row that was clicked -- see AddRankTrackerKeywordsParams.
          keywordIds: [keyword.id],
        },
        {
          onSuccess: () => {
            toast({ title: "Added to Rank Tracker", description: `"${keyword.search}" is now tracked.` });
            queryClient.invalidateQueries({ queryKey: keywordApi.getKeywordsQueryPrefix(productId) });
          },
        },
      );
    },
    [addRankTrackerKeywords, productId, selectedBrandId],
  );

  const removeFromRankTracker = useCallback(
    (keyword: Keyword) => {
      if (!selectedBrandId) return;
      deleteRankTrackerKeyword(
        { id: keyword.id, brandId: selectedBrandId },
        {
          onSuccess: () => {
            toast({ title: "Removed from Rank Tracker", description: `"${keyword.search}" is no longer tracked.` });
            queryClient.invalidateQueries({ queryKey: keywordApi.getKeywordsQueryPrefix(productId) });
          },
        },
      );
    },
    [deleteRankTrackerKeyword, productId, selectedBrandId],
  );

  const addToTheme = useCallback(
    async (keywordId: number, themeId: number) => {
      try {
        await addKeywordToTheme({ productId, themeId, keywordId });
      } catch {
        // useAddKeywordToTheme's own onError (handleApiError) already shows a
        // toast on failure -- just swallow here so the promise doesn't
        // surface as an unhandled rejection (mirrors handleChangeKeywordTheme,
        // KeywordBankContentV2.tsx:503-507).
      }
    },
    [addKeywordToTheme, productId],
  );

  return { toggleMasterList, addLandscape, addToRankTracker, removeFromRankTracker, addToTheme };
}
